import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  Typography,
  Chip,
} from "@mui/material";

import { Loading } from "../../components/Loading";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { isBrowser } from "react-device-detect";
import { SupportServer } from "../../interfaces/SupportServer";
import { SupportServerDialog } from "./SupportServerDialog";

interface Props {
  serverList: SupportServer[];
  dialogMode: string;
}

export const SupportServerTable = ({ serverList, dialogMode }: Props) => {
  const [server, setServer] = useState<SupportServer | null>(null);
  const [showDialog, setShowDialog] = useState(dialogMode === "add");

  const { t } = useTranslation();
  const theme = useTheme();
  const fontVariant = isBrowser ? "body2" : "caption";

  if (!serverList) {
    return <Loading />;
  }

  const getServerTypeLabel = (type: string) => {
    switch (type) {
      case "webrtc":
        return "WebRTC";
      case "jitsi":
        return "Jitsi";
      case "ai":
        return "AI";
      case "sso":
        return "SSO";
      case "ehrbase":
        return "EHRbase";
      default:
        return type;
    }
  };

  return (
    <TableContainer component={Paper}>
      {server && (
        <SupportServerDialog
          openState={showDialog}
          setOpenState={setShowDialog}
          targetServer={server}
          dialogMode={"edit"}
        />
      )}

      <Table aria-label="simple table">
        <TableHead>
          <TableRow
            sx={{
              border: 0,
              bgcolor: theme.palette.primary.main,
              "& th": {
                color: theme.palette.text.secondary,
              },
            }}
          >
            <TableCell
              sx={{
                padding: "16px 6px",
              }}
            >
              {t("server_name")}
            </TableCell>

            <TableCell
              sx={{
                padding: "16px 6px",
              }}
            >
              {t("server_type")}
            </TableCell>

            <TableCell
              sx={{
                padding: "16px 6px",
              }}
            >
              {t("server_url")}
            </TableCell>

            <TableCell
              sx={{
                padding: "16px 6px",
              }}
            >
              {t("status")}
            </TableCell>

            <TableCell
              sx={{
                padding: "16px 6px",
              }}
            >
              {t("last_updated")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {serverList?.map((server: SupportServer, i: number) => (
            <TableRow
              onClick={() => {
                setServer(server);
                setShowDialog(true);
              }}
              key={i}
              hover
              sx={{
                "&:nth-of-type(odd)": {
                  background: theme.palette.info.dark,
                },
                "&:nth-of-type(even)": {
                  background: theme.palette.info.light,
                },
                cursor: "pointer",
              }}
            >
              <TableCell
                sx={{
                  padding: "16px 6px",
                }}
              >
                <Typography variant={fontVariant}>
                  {server.server_name}
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  padding: "16px 6px",
                }}
              >
                <Typography variant={fontVariant}>
                  {getServerTypeLabel(server.server_type)}
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  padding: "16px 6px",
                }}
              >
                <Typography variant={fontVariant}>
                  {server.server_url}
                </Typography>
              </TableCell>

              <TableCell
                sx={{
                  padding: "16px 6px",
                }}
              >
                <Chip
                  label={server.is_active ? t("active") : t("inactive")}
                  color={server.is_active ? "success" : "default"}
                  size="small"
                />
              </TableCell>

              <TableCell
                sx={{
                  padding: "16px 6px",
                }}
              >
                <Typography variant={fontVariant}>
                  {server.updated_at
                    ? new Date(server.updated_at).toLocaleString()
                    : "-"}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
